/**
 * Condition evaluation utilities for decorator steps.
 * Extracted from DecoratorClass to be shared across all steps.
 */
import type {
    DependsOnCondition,
    SingleDependsOnCondition,
    OrConditionItem,
    AndConditionItem
} from '@sap/ux-specification-types';
import { JSONPath } from 'jsonpath-plus';
import { isPathNode, getPath, isAndConditionGroup, isOrConditionGroup } from './decorator-paths';
import type { AppContext, PageContext } from './decorator-paths';
import type { DecoratorContext } from './interfaces';

/**
 * Internal evaluation context — plain data objects for JSONPath resolution.
 * Uses app.config and page.config rather than the full App/Page instances.
 */
type EvaluationContext = {
    app?: AppContext;
    page?: PageContext;
    custom?: Record<string, unknown>;
};

/**
 * Result of evaluating a single condition.
 */
export type EvaluateConditionResult = {
    passed: boolean;
    value: unknown;
    key: string;
};

/**
 * Result of evaluating a condition with context for messages.
 */
export type ConditionEvaluationResult = {
    passed: boolean;
    context: string;
};

/**
 * Evaluates decorator conditions against runtime context.
 * Supports single conditions, AND conditions, and OR conditions with nesting.
 */
export class ConditionEvaluator {
    private readonly context: EvaluationContext;

    /**
     * Creates a new ConditionEvaluator with the given decorator context.
     *
     * @param context - The decorator context containing app, page, and custom runtime data
     */
    constructor(context: DecoratorContext) {
        this.context = {
            // Use app.config (plain AppContext) for JSONPath evaluation, not the full App instance.
            // app.config holds manifest and other plain data that path builders reference (e.g. app.manifest).
            app: context.app?.config,
            page: context.page?.config,
            custom: context.custom
        };
    }

    /**
     * Gets property value from the decorator context using a property path.
     * Supports JSONPath syntax for complex paths.
     *
     * @param propertyPath - JSONPath-style path to resolve (e.g. 'page.isALP', 'custom.type')
     * @returns The key/value pair at the resolved path, or undefined if not found
     */
    getPropertyKeyValue(propertyPath: string): { key: string; value: unknown } | undefined {
        if (!propertyPath || !this.context) {
            return undefined;
        }

        try {
            const jsonPath = propertyPath.startsWith('$') ? propertyPath : `$.${propertyPath}`;
            const results = JSONPath({
                path: jsonPath,
                json: this.context,
                resultType: 'all'
            });

            if (!results || results.length === 0) {
                return undefined;
            }

            const result = results[0];
            const key = String(result.parentProperty);
            const value = result.value;

            return { key, value };
        } catch {
            return undefined;
        }
    }

    /**
     * Resolves PathNode values in message params to their actual values.
     *
     * @param params - Optional map of parameter names to values (may contain PathNodes)
     * @returns Resolved params with PathNodes replaced by their runtime values
     */
    resolveMessageParams(params?: Record<string, unknown>): Record<string, unknown> | undefined {
        if (!params) {
            return params;
        }

        const resolved: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(params)) {
            if (isPathNode(value)) {
                const result = this.getPropertyKeyValue(getPath(value));
                resolved[key] = result?.value ?? '';
            } else {
                resolved[key] = value;
            }
        }
        return resolved;
    }

    /**
     * Evaluates a condition and returns whether it passed and context for messages.
     *
     * @param conditionInfo - The condition to evaluate (single, AND, or OR)
     * @returns Whether the condition passed and a context string for message interpolation
     */
    evaluate(conditionInfo: DependsOnCondition): ConditionEvaluationResult {
        let passed = false;
        let context = '';

        if (conditionInfo.orConditions && Array.isArray(conditionInfo.orConditions)) {
            const result = this.evaluateOrConditions(conditionInfo.orConditions);
            passed = result.passed;
            context = this.getContextForMessage(result.results);
        } else if (conditionInfo.conditions && Array.isArray(conditionInfo.conditions)) {
            const result = this.evaluateAndConditions(conditionInfo.conditions);
            passed = result.passed;
            context = this.getContextForMessage(result.results);
        } else if (conditionInfo.path) {
            const result = this.evaluateSingleCondition(conditionInfo as SingleDependsOnCondition);
            passed = result.passed;
            context = this.getContextForMessage([result]);
        }

        return { passed, context };
    }

    /**
     * Evaluates a single dependency condition.
     *
     * @param condition - A single condition with path, optional expectedValue, and optional negate
     * @returns The evaluation result with passed flag, resolved value, and key
     */
    private evaluateSingleCondition(condition: SingleDependsOnCondition): EvaluateConditionResult {
        if (condition.path === '__always__') {
            return { passed: true, value: true, key: '__always__' };
        }

        const result = this.getPropertyKeyValue(condition.path);
        const key = result?.key ?? condition.path;
        const value = result?.value;

        let passed = false;

        if (condition.dependsOn) {
            passed = condition.dependsOn(value);
        } else if (condition.expectedValue !== undefined) {
            passed = value === condition.expectedValue;
        } else {
            passed = !!value;
        }

        if (condition.negate) {
            passed = !passed;
        }

        return { passed, value, key };
    }

    /**
     * Gets the context message from evaluation results.
     *
     * @param results - Array of evaluation results to summarize
     * @returns A comma-separated string of failed condition key/value pairs
     */
    private getContextForMessage(results: EvaluateConditionResult[]): string {
        return results
            .filter((result) => !result.passed)
            .map((result) => `${result.key}: ${result.value}`)
            .join(', ');
    }

    /**
     * Evaluates an AND condition item which can be a single condition or nested OR group.
     *
     * @param conditionItem - A single condition or an OR group nested inside AND
     * @returns Whether the item passed and the individual evaluation results
     */
    private evaluateAndConditionItem(conditionItem: AndConditionItem): {
        passed: boolean;
        results: EvaluateConditionResult[];
    } {
        if (isOrConditionGroup(conditionItem)) {
            return this.evaluateOrConditions(conditionItem.__orConditions);
        }

        const result = this.evaluateSingleCondition(conditionItem);
        return { passed: result.passed, results: [result] };
    }

    /**
     * Evaluates OR conditions (at least one must match).
     *
     * @param orConditions - Array of OR condition items (single conditions or AND groups)
     * @returns Whether any condition passed and all individual evaluation results
     */
    private evaluateOrConditions(orConditions: OrConditionItem[]): {
        passed: boolean;
        results: EvaluateConditionResult[];
    } {
        const allResults: EvaluateConditionResult[] = [];

        for (const conditionItem of orConditions) {
            if (isAndConditionGroup(conditionItem)) {
                const andResults: EvaluateConditionResult[] = [];
                let allAndPassed = true;

                for (const andItem of conditionItem.__andConditions) {
                    const { passed, results } = this.evaluateAndConditionItem(andItem);
                    andResults.push(...results);
                    if (!passed) {
                        allAndPassed = false;
                    }
                }

                allResults.push(...andResults);

                if (allAndPassed) {
                    return { passed: true, results: allResults };
                }
            } else {
                const result = this.evaluateSingleCondition(conditionItem);
                allResults.push(result);

                if (result.passed) {
                    return { passed: true, results: allResults };
                }
            }
        }

        return { passed: false, results: allResults };
    }

    /**
     * Evaluates AND conditions (all must match).
     *
     * @param andConditions - Array of AND condition items (single conditions or OR groups)
     * @returns Whether all conditions passed and all individual evaluation results
     */
    private evaluateAndConditions(andConditions: AndConditionItem[]): {
        passed: boolean;
        results: EvaluateConditionResult[];
    } {
        const allResults: EvaluateConditionResult[] = [];
        let allPassed = true;

        for (const conditionItem of andConditions) {
            const { passed, results } = this.evaluateAndConditionItem(conditionItem);
            allResults.push(...results);

            if (!passed) {
                allPassed = false;
            }
        }

        return { passed: allPassed, results: allResults };
    }
}
